/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  Descriptions,
  Input,
  Message,
  Modal,
  Select,
  Space,
  Table,
  TableColumnProps,
  Typography
} from '@arco-design/web-react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { get } from '../requestNew';
import { AppleIapNotificationItem, BIZ_CATEGORY_OPTIONS, bizCategoryLabelByValue } from './types';
import './index.scss';

const Option = Select.Option;
const RangePicker = DatePicker.RangePicker;

type FilterState = {
  notificationUuid: string;
  transactionId: string;
  appAccountToken: string;
  bizCategory: string;
  purchaseTimeStart: string;
  purchaseTimeEnd: string;
};

const emptyFilters: FilterState = {
  notificationUuid: '',
  transactionId: '',
  appAccountToken: '',
  bizCategory: '',
  purchaseTimeStart: '',
  purchaseTimeEnd: ''
};

function formatTime(value?: string) {
  return value && dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-';
}

function formatPrice(milli?: number, currency?: string) {
  if (milli == null) return '-';
  const amount = (milli / 1000).toFixed(2);
  return currency ? `${amount} ${currency}` : amount;
}

function formatPayload(payload?: string | Record<string, unknown>) {
  if (payload == null || payload === '') return '';
  if (typeof payload === 'object') return JSON.stringify(payload, null, 2);
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch (e) {
    return payload;
  }
}

function AppleIapNotification() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [dataList, setDataList] = useState<AppleIapNotificationItem[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const isInitialMount = useRef(true);

  const [detailVisible, setDetailVisible] = useState(false);
  const [detail, setDetail] = useState<AppleIapNotificationItem | null>(null);

  const buildParams = (page: number, size: number, f: FilterState) => {
    const params: Record<string, string | number> = {
      pageNum: page,
      pageSize: size
    };
    if (f.notificationUuid.trim()) params.notificationUuid = f.notificationUuid.trim();
    if (f.transactionId.trim()) params.transactionId = f.transactionId.trim();
    if (f.appAccountToken.trim()) params.appAccountToken = f.appAccountToken.trim();
    if (f.bizCategory) params.bizCategory = f.bizCategory;
    if (f.purchaseTimeStart) params.purchaseTimeStart = f.purchaseTimeStart;
    if (f.purchaseTimeEnd) params.purchaseTimeEnd = f.purchaseTimeEnd;
    return params;
  };

  const getList = (opts?: { pageNum?: number; pageSize?: number; filters?: FilterState }) => {
    const nextPage = opts?.pageNum ?? pageNum;
    const nextSize = opts?.pageSize ?? pageSize;
    const nextFilters = opts?.filters ?? filters;
    setLoading(true);
    get('/sys/umsAppleIapNotification/list', buildParams(nextPage, nextSize, nextFilters))
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          setDataList(data?.list ?? data?.records ?? []);
          setTotal(data?.total ?? 0);
        } else {
          Message.error(message || '查询失败');
        }
      })
      .catch(() => Message.error('网络错误'))
      .finally(() => setLoading(false));
  };

  const patchFilter = (patch: Partial<FilterState>, autoQuery = false) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    if (autoQuery) {
      setPageNum(1);
      getList({ pageNum: 1, filters: next });
    }
  };

  const handleQuery = () => {
    setPageNum(1);
    getList({ pageNum: 1 });
  };

  const handleReset = () => {
    setFilters(emptyFilters);
    setPageNum(1);
    getList({ pageNum: 1, filters: emptyFilters });
  };

  const openOrder = (token?: string) => {
    if (!token) return;
    navigate(`/pay-order?orderId=${encodeURIComponent(token)}`);
  };

  const columns: TableColumnProps<AppleIapNotificationItem>[] = [
    {
      title: '通知UUID',
      dataIndex: 'notificationUuid',
      width: 220,
      render: v => v || '-'
    },
    {
      title: '通知类型',
      dataIndex: 'notificationType',
      width: 180,
      render: v => v || '-'
    },
    {
      title: '子类型',
      dataIndex: 'subtype',
      width: 140,
      render: v => v || '-'
    },
    {
      title: '业务分类',
      dataIndex: 'bizCategory',
      width: 110,
      render: v => bizCategoryLabelByValue[v] || v || '-'
    },
    {
      title: '环境',
      dataIndex: 'environment',
      width: 110,
      render: v => v || '-'
    },
    {
      title: '商品ID',
      dataIndex: 'productId',
      width: 160,
      render: v => v || '-'
    },
    {
      title: '交易号',
      dataIndex: 'transactionId',
      width: 180,
      render: v => v || '-'
    },
    {
      title: '订单号',
      dataIndex: 'appAccountToken',
      width: 280,
      render: v =>
        v ? (
          <span className="token-link" onClick={() => openOrder(v)}>
            {v}
          </span>
        ) : (
          '-'
        )
    },
    {
      title: '金额',
      dataIndex: 'priceMilli',
      width: 120,
      render: (_v, record) => formatPrice(record.priceMilli, record.currency)
    },
    {
      title: '购买时间',
      dataIndex: 'purchaseTime',
      width: 180,
      render: v => formatTime(v)
    },
    {
      title: '操作',
      dataIndex: 'operation',
      width: 90,
      fixed: 'right',
      render: (_text, record) => (
        <Button
          type="text"
          size="small"
          onClick={() => {
            setDetail(record);
            setDetailVisible(true);
          }}
        >
          详情
        </Button>
      )
    }
  ];

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      getList({ pageNum: 1 });
      return;
    }
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, pageSize]);

  const payloadText = formatPayload(detail?.decodedPayload);

  return (
    <div className="apple-iap-notification table-flex">
      <Space style={{ flexWrap: 'wrap' }}>
        <div className="filter-item">
          <Typography.Text>通知UUID:</Typography.Text>
          <Input
            value={filters.notificationUuid}
            onChange={v => patchFilter({ notificationUuid: v })}
            style={{ width: 220 }}
            allowClear
          />
        </div>
        <div className="filter-item">
          <Typography.Text>交易号:</Typography.Text>
          <Input
            value={filters.transactionId}
            onChange={v => patchFilter({ transactionId: v })}
            style={{ width: 180 }}
            allowClear
          />
        </div>
        <div className="filter-item">
          <Typography.Text>订单号:</Typography.Text>
          <Input
            value={filters.appAccountToken}
            onChange={v => patchFilter({ appAccountToken: v })}
            style={{ width: 260 }}
            allowClear
            placeholder="app_account_token"
          />
        </div>
        <div className="filter-item">
          <Typography.Text>业务分类:</Typography.Text>
          <Select
            value={filters.bizCategory || undefined}
            onChange={v => patchFilter({ bizCategory: v || '' }, true)}
            style={{ width: 140 }}
            allowClear
            placeholder="全部"
          >
            {BIZ_CATEGORY_OPTIONS.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
        <div className="filter-item">
          <Typography.Text>购买时间:</Typography.Text>
          <RangePicker
            showTime
            style={{ width: 380 }}
            format="YYYY-MM-DD HH:mm:ss"
            value={
              filters.purchaseTimeStart && filters.purchaseTimeEnd
                ? [filters.purchaseTimeStart, filters.purchaseTimeEnd]
                : undefined
            }
            onChange={dateString => {
              const start = dateString?.[0] || '';
              const end = dateString?.[1] || '';
              patchFilter({ purchaseTimeStart: start, purchaseTimeEnd: end }, true);
            }}
          />
        </div>
        <Button className="filter-item" type="primary" onClick={handleQuery}>
          查询
        </Button>
        <Button className="filter-item" type="outline" onClick={handleReset}>
          重置
        </Button>
      </Space>

      <div className="table-content">
        <Table
          loading={loading}
          columns={columns}
          data={dataList}
          pagination={{
            current: pageNum,
            pageSize,
            total,
            sizeCanChange: true,
            sizeOptions: [20, 50, 100],
            showTotal: true,
            onChange: (page, size) => {
              setPageNum(page);
              if (size && size !== pageSize) {
                setPageSize(size);
              }
            }
          }}
          rowKey="id"
          className="table-scroll"
          scroll={{ y: '100%', x: '100%' }}
        />
      </div>

      <Modal
        title="苹果IAP通知详情"
        visible={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        style={{ width: 860 }}
        unmountOnExit
      >
        {detail ? (
          <>
            <Descriptions
              column={2}
              data={[
                { label: 'ID', value: detail.id ?? '-' },
                { label: '通知UUID', value: detail.notificationUuid || '-' },
                { label: '通知类型', value: detail.notificationType || '-' },
                { label: '子类型', value: detail.subtype || '-' },
                {
                  label: '业务分类',
                  value: bizCategoryLabelByValue[detail.bizCategory || ''] || detail.bizCategory || '-'
                },
                { label: '环境', value: detail.environment || '-' },
                { label: 'Bundle ID', value: detail.bundleId || '-' },
                { label: '商品ID', value: detail.productId || '-' },
                { label: '交易号', value: detail.transactionId || '-' },
                { label: '原始交易号', value: detail.originalTransactionId || '-' },
                {
                  label: '订单号',
                  value: detail.appAccountToken ? (
                    <span className="token-link" onClick={() => openOrder(detail.appAccountToken)}>
                      {detail.appAccountToken}
                    </span>
                  ) : (
                    '-'
                  )
                },
                { label: '金额', value: formatPrice(detail.priceMilli, detail.currency) },
                { label: '标价(毫)', value: detail.priceMilli ?? '-' },
                { label: '币种', value: detail.currency || '-' },
                { label: '数量', value: detail.quantity ?? '-' },
                { label: '购买时间', value: formatTime(detail.purchaseTime) },
                { label: '撤销时间', value: formatTime(detail.revocationTime) },
                { label: '撤销原因', value: detail.revocationReason || '-' },
                { label: '签名时间', value: formatTime(detail.signedDate) },
                { label: '创建时间', value: formatTime(detail.createTime) },
                { label: '更新时间', value: formatTime(detail.updateTime) }
              ]}
            />
            {payloadText ? <pre className="payload-block">{payloadText}</pre> : null}
          </>
        ) : null}
      </Modal>
    </div>
  );
}

export default AppleIapNotification;
